import { memo } from 'react'
import type { MutableRefObject } from 'react'

import { datum, tonnen, wochentag } from '../format'
import { bloecke } from '../hilfen'
import type { Kladde, KladdeSpalte } from '../typen'

interface Eigenschaften {
  kladde: Kladde
  /** Der scrollende Rahmen - die Seite merkt sich darüber, wo man in ihm stand (waagerecht und senkrecht). */
  rahmen: MutableRefObject<HTMLDivElement | null>
}

/** Hintergrund einer Spalte: Produktion und Summe heben sich von den Bestandsspalten ab. */
function zellenklasse(spalte: KladdeSpalte): string {
  if (spalte.art === 'produktion') return 'gruppe-produktion'
  if (spalte.art === 'summe') return 'gruppe-summe'
  return ''
}


/**
 * Bildet ein Blatt der Excel-Kladde ab: pro Tag eine Zeile, je Fertigartikel ein Block aus
 * Tagesproduktion und den Materialien, die nur er zieht. Gemeinsam genutztes Material steht
 * hinten in einem eigenen Block. Alle Werte in Tonnen Fertigprodukt.
 *
 * Bewusst eine echte HTML-Tabelle: nur so lassen sich Zellen markieren und nach Excel kopieren.
 * Kopfzeilen und Tagesspalte bleiben über CSS stehen.
 */
// memo: beim Neuladen bleibt die alte Tabelle mit denselben Daten stehen - ohne memo baute sie sich dabei neu auf.
export default memo(function KladdeTabelle({ kladde, rahmen }: Eigenschaften) {
  const spalten = kladde.spalten
  // Vermerke je Zelle (Tag + Artikel) und je Spalte (ohne Tag); Wareneingaenge je Zelle.
  const vermerkeJeZelle = new Map<string, string[]>()
  const vermerkeJeSpalte = new Map<string, string[]>()
  for (const v of kladde.vermerke ?? []) {
    if (!v.artikelnummer) continue
    const text = `${v.art}: ${v.text}`
    if (v.tag) {
      const bis = v.bis ?? v.tag
      for (const zeile of kladde.zeilen) {
        if (zeile.tag >= v.tag && zeile.tag <= bis) {
          const schluessel = `${zeile.tag}|${v.artikelnummer}`
          vermerkeJeZelle.set(schluessel, [...(vermerkeJeZelle.get(schluessel) ?? []), text])
        }
      }
    } else {
      vermerkeJeSpalte.set(v.artikelnummer, [...(vermerkeJeSpalte.get(v.artikelnummer) ?? []), text])
    }
  }
  const ereignisseJeZelle = new Map<string, string[]>()
  // Welche Marke die Zelle trägt: WE für einen Wareneingang, INV für eine Inventur, K für eine Korrektur - ein
  // Inventurtag hieß bisher „WE", das war falsch (18.09.2026).
  const markenJeZelle = new Map<string, Set<string>>()
  for (const e of kladde.ereignisse ?? []) {
    const schluessel = `${e.tag}|${e.artikelnummer}`
    const text = e.art === 'zugang' ? `WE ${tonnen(e.menge_t)} t${e.beleg ? ` (${e.beleg})` : ''}` : e.art === 'inventur' ? `Inventur ${tonnen(e.menge_t)} t` : `Korrektur ${tonnen(e.menge_t)} t`
    ereignisseJeZelle.set(schluessel, [...(ereignisseJeZelle.get(schluessel) ?? []), text])
    markenJeZelle.set(schluessel, new Set([...(markenJeZelle.get(schluessel) ?? []), e.art === 'zugang' ? 'WE' : e.art === 'inventur' ? 'INV' : 'K']))
  }

  if (spalten.length === 0) {
    return (
      <p className="leer">
        Für diese Gruppe sind noch keine Artikel hinterlegt. Stammdaten anlegen, dann füllt sich die
        Tabelle mit dem nächsten Import.
      </p>
    )
  }

  return (
    <div className="tabellenrahmen" ref={rahmen}>
      <table>
        <thead>
          <tr>
            <th className="tag-spalte h1" rowSpan={3}>
              Tag
            </th>
            {bloecke(spalten, (s) => s.gruppe).map((block, index) => (
              <th key={`${block.titel}-${index}`} className="h1 block" colSpan={block.breite}>
                {block.titel}
              </th>
            ))}
          </tr>
          <tr>
            {spalten.map((spalte) => (
              <th
                key={spalte.schluessel}
                className={`h2 zahl ${zellenklasse(spalte)}`}
              >
                {spalte.art === 'produktion' ? 'Tagesproduktion' : spalte.bezeichnung}
              </th>
            ))}
          </tr>
          <tr>
            {spalten.map((spalte) => (
              <th
                key={spalte.schluessel}
                className={`h3 zahl ${zellenklasse(spalte)}`}
              >
                {spalte.art === 'summe' ? 'aus Regel' : spalte.artikelnummer}
                {spalte.hersteller && <span className="hinweis"> · {spalte.hersteller}</span>}
                {vermerkeJeSpalte.has(spalte.artikelnummer) && (
                  <span className="vermerk-marke" title={vermerkeJeSpalte.get(spalte.artikelnummer)!.join('\n')}>
                    Vermerk
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {kladde.zeilen.map((zeile) => (
            <tr key={zeile.tag}>
              <td className="tag-spalte">
                {wochentag(zeile.tag)} {datum(zeile.tag)}
              </td>
              {spalten.map((spalte) => {
                const wert = zeile.werte[spalte.schluessel] ?? null
                const knapp =
                  spalte.art === 'bestand' &&
                  spalte.mindestbestand_t !== null &&
                  wert !== null &&
                  wert < spalte.mindestbestand_t
                const schluessel = `${zeile.tag}|${spalte.artikelnummer}`
                const vermerke = vermerkeJeZelle.get(schluessel) ?? []
                const ereignisse = ereignisseJeZelle.get(schluessel) ?? []
                const hinweise = [
                  ...(knapp ? [`unter Mindestbestand ${tonnen(spalte.mindestbestand_t)} t`] : []),
                  ...ereignisse,
                  ...vermerke,
                ]
                return (
                  <td
                    key={spalte.schluessel}
                    className={`zahl ${zellenklasse(spalte)} ${knapp ? 'unter-mindestbestand' : ''} ${vermerke.length > 0 ? 'vermerk-zelle' : ''} ${ereignisse.length > 0 ? 'we-zelle' : ''}`}
                    title={hinweise.length > 0 ? hinweise.join('\n') : undefined}
                  >
                    {tonnen(wert)}
                    {knapp && ' !'}
                    {[...(markenJeZelle.get(schluessel) ?? [])].map((m) => <span key={m} className={`marke ${m === 'WE' ? 'we' : 'inv'}`}>{m}</span>)}
                    {vermerke.length > 0 && <span className="marke vermerk">V</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {kladde.zeilen.length === 0 && (
        <p className="leer">Für den gewählten Zeitraum gibt es keine Zeilen.</p>
      )}
    </div>
  )
})
